/**
 * Product page: gallery, viewer, sizes, add to bag, tabs, Wear It With and
 * the sticky bar.
 *
 * The markup does the work without this: sizes are buttons in a real product
 * form, the gallery scrolls and snaps on its own, and the swatches are links.
 * This keeps them in step with each other and asks for a size before anything
 * reaches the bag, so nobody buys the smallest size by accident.
 */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  const setup = (section) => {
    if (section.dataset.nylReady) return;
    section.dataset.nylReady = 'true';

    const handle = section.dataset.productHandle;
    const inputs = () => section.querySelectorAll('[data-nyl-variant-input]');
    const chosenId = () => section.querySelector('[data-nyl-variant-input]')?.value || '';
    // Set below, where the bag is read. Choosing a size calls it.
    let showInBag = () => {};

    /* Rating ------------------------------------------------------------- */

    // The stars jump down to the reviews. Until a reviews app sits on the
    // page there is nothing to land on, so the link stands down to text.
    section.querySelectorAll('[data-nyl-rating-link]').forEach((link) => {
      const hash = link.getAttribute('href') || '';
      if (!hash.startsWith('#')) return;
      let target = null;
      try {
        target = hash.length > 1 ? document.querySelector(hash) : null;
      } catch (error) {
        target = null;
      }
      if (!target) link.removeAttribute('href');
    });

    /* Gallery ------------------------------------------------------------ */

    // Keeps a thumbnail in view inside its own strip. scrollIntoView moves
    // every scrolling box above it as well, the page included, so a product
    // page used to open part way down, just far enough to show the first
    // thumbnail whole. This only ever scrolls the strip, by the distance the
    // thumbnail sits outside it, which also holds for right to left pages.
    const revealInStrip = (thumb) => {
      const strip = thumb?.closest('ul');
      if (!strip) return;
      const box = strip.getBoundingClientRect();
      const item = thumb.getBoundingClientRect();
      if (item.left < box.left) strip.scrollLeft -= box.left - item.left;
      else if (item.right > box.right) strip.scrollLeft += item.right - box.right;
    };

    const track = section.querySelector('[data-nyl-track]');
    const slides = track ? [...track.querySelectorAll('[data-nyl-slide]')] : [];
    const counter = section.querySelector('[data-nyl-counter]');
    const thumbs = [...section.querySelectorAll('[data-nyl-thumb]')];
    const prev = section.querySelector('[data-nyl-gallery-prev]');
    const next = section.querySelector('[data-nyl-gallery-next]');
    let index = 0;

    const indexOf = (scroller) => {
      const width = scroller.clientWidth || 1;
      return Math.round(scroller.scrollLeft / width);
    };

    const showIndex = (i) => {
      index = i;
      if (counter) counter.textContent = String(i + 1);
      thumbs.forEach((thumb, n) => {
        const on = n === i;
        thumb.classList.toggle('is-current', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= slides.length - 1;
      // Keep the current thumbnail in view when the strip scrolls.
      revealInStrip(thumbs[i]);
    };

    const goTo = (i, smooth = true) => {
      if (!track || !slides.length) return;
      const target = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      showIndex(target);
    };

    if (track) {
      let frame;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const i = indexOf(track);
          if (i !== index) showIndex(i);
        });
      }, { passive: true });
      showIndex(0);
    }

    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.index))));

    /* Viewer ------------------------------------------------------------- */

    const viewer = section.querySelector('[data-nyl-viewer]');
    if (viewer) {
      const vTrack = viewer.querySelector('[data-nyl-viewer-track]');
      const vIndex = viewer.querySelector('[data-nyl-viewer-index]');
      const vThumbs = [...viewer.querySelectorAll('[data-nyl-viewer-thumb]')];
      const vPrev = viewer.querySelector('[data-nyl-viewer-prev]');
      const vNext = viewer.querySelector('[data-nyl-viewer-next]');
      const count = vTrack ? vTrack.children.length : 0;
      let vi = 0;

      const vShow = (i) => {
        vi = i;
        if (vIndex) vIndex.textContent = String(i + 1);
        vThumbs.forEach((thumb, n) => thumb.classList.toggle('is-current', n === i));
        if (vPrev) vPrev.disabled = i <= 0;
        if (vNext) vNext.disabled = i >= count - 1;
        revealInStrip(vThumbs[i]);
      };

      // The frame takes the current picture's proportions, so it shows whole
      // with no empty bands. The width changes with it, so the track is put
      // back on the picture straight after.
      const fitFrame = (i) => {
        if (!vTrack) return;
        const ratio = Number(vTrack.children[i]?.dataset.ratio) || 0.6667;
        viewer.style.setProperty('--nyl-viewer-ratio', String(ratio));
        vTrack.scrollTo({ left: i * vTrack.clientWidth, behavior: 'auto' });
      };

      const vGo = (i, smooth = true) => {
        if (!vTrack) return;
        const target = Math.max(0, Math.min(count - 1, i));
        const sameShape = vTrack.children[target]?.dataset.ratio === vTrack.children[vi]?.dataset.ratio;
        if (smooth && sameShape) {
          vTrack.scrollTo({ left: target * vTrack.clientWidth, behavior: 'smooth' });
        } else {
          fitFrame(target);
        }
        vShow(target);
      };

      // A swipe settles first; only then does the frame change shape.
      let vFrame;
      let vSettle;
      vTrack?.addEventListener('scroll', () => {
        cancelAnimationFrame(vFrame);
        vFrame = requestAnimationFrame(() => {
          const i = indexOf(vTrack);
          if (i !== vi) vShow(i);
        });
        window.clearTimeout(vSettle);
        vSettle = window.setTimeout(() => {
          const ratio = Number(vTrack.children[vi]?.dataset.ratio) || 0.6667;
          if (String(ratio) !== viewer.style.getPropertyValue('--nyl-viewer-ratio')) fitFrame(vi);
        }, 140);
      }, { passive: true });

      section.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-nyl-open-viewer]');
        if (!opener) return;
        // A button another app puts on the picture opens its own window, not
        // the viewer as well. Contona's Try This On sits inside this button
        // since 2026-10-12, and its tap comes up from a Shadow DOM, so the
        // tapped element isn't the page's own.
        if (event.composedPath()[0] !== event.target || event.target.closest('[data-contona-host], #__contona-tryon-btn')) return;
        viewer.showModal();
        // Opened where the visitor was looking, without a slide across.
        requestAnimationFrame(() => vGo(Number(opener.dataset.index), false));
      });

      vPrev?.addEventListener('click', () => vGo(vi - 1));
      vNext?.addEventListener('click', () => vGo(vi + 1));
      vThumbs.forEach((thumb) => thumb.addEventListener('click', () => vGo(Number(thumb.dataset.index))));
      viewer.querySelector('[data-nyl-viewer-close]')?.addEventListener('click', () => viewer.close());

      viewer.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); vGo(vi + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); vGo(vi - 1); }
      });

      // A click on the backdrop, outside the frame, closes it.
      viewer.addEventListener('click', (event) => {
        if (event.target === viewer) viewer.close();
      });

      // Back on the page, the gallery shows the picture the viewer ended on.
      viewer.addEventListener('close', () => goTo(vi, false));
    }

    /* Sizes -------------------------------------------------------------- */

    const sizes = [...section.querySelectorAll('[data-nyl-size]')];
    const sizeField = section.querySelector('[data-nyl-sizes]');
    const sizeError = section.querySelector('[data-nyl-size-error]');
    const price = section.querySelector('[data-nyl-price]');
    const priceWas = section.querySelector('[data-nyl-price-was]');
    const stickyVariant = section.querySelector('[data-nyl-sticky-variant]');
    const stickyPrice = section.querySelector('[data-nyl-sticky-price]');

    const clearSizeError = () => {
      if (sizeError) sizeError.hidden = true;
      sizeField?.classList.remove('is-invalid');
    };

    const askForSize = (scroll = false) => {
      if (!sizeField) return;
      if (sizeError) sizeError.hidden = false;
      sizeField.classList.add('is-invalid');
      if (scroll) sizeField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      sizeField.querySelector('[data-nyl-size]:not(:disabled)')?.focus({ preventScroll: scroll });
    };

    const choose = (button) => {
      sizes.forEach((other) => {
        const on = other === button;
        other.classList.toggle('is-chosen', on);
        other.setAttribute('aria-pressed', String(on));
      });
      inputs().forEach((input) => { input.value = button.dataset.variantId; });
      if (price) price.textContent = button.dataset.price;
      if (priceWas) {
        priceWas.textContent = button.dataset.compare || '';
        priceWas.hidden = !button.dataset.compare;
      }
      if (stickyVariant) stickyVariant.textContent = button.dataset.size;
      if (stickyPrice) stickyPrice.textContent = button.dataset.price;
      section.querySelectorAll('[data-nyl-sticky-size-option]').forEach((option) => {
        const on = option.dataset.variantId === button.dataset.variantId;
        option.classList.toggle('is-chosen', on);
        option.setAttribute('aria-pressed', String(on));
      });

      // The heart on the image saves this size with the product.
      section.querySelectorAll('[data-nyl-wishlist-toggle]').forEach((heart) => {
        if (heart.closest('.nyl-product__gallery')) heart.dataset.variantId = button.dataset.variantId;
      });

      // The address keeps the size, so a shared or refreshed page keeps it.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', button.dataset.variantId);
      url.searchParams.delete('size');
      window.history.replaceState(window.history.state, '', url.toString());

      // The other colourways carry the size along, so it stays chosen when
      // the visitor changes colour.
      section.querySelectorAll('[data-nyl-colour-link]').forEach((link) => {
        if (link.getAttribute('aria-current') === 'true') return;
        const target = new URL(link.href, window.location.origin);
        if (button.dataset.code) target.searchParams.set('size', button.dataset.code);
        link.href = target.pathname + target.search;
      });
      clearSizeError();
      showInBag();

      // The Added to bag panel fetches its view of this size now, so it can
      // open the moment the size is added (nyl-quick-add.js).
      window.nylPanelPrefetch?.(handle, button.dataset.variantId);
    };

    sizes.forEach((button) => button.addEventListener('click', () => {
      if (!button.disabled) choose(button);
    }));

    // Arriving from another colourway with a size already chosen: the same
    // size is chosen here too, if this colour has it in stock.
    const carried = new URLSearchParams(window.location.search);
    const carriedSize = (carried.get('size') || '').trim().toLowerCase();
    if (carriedSize && !carried.get('variant')) {
      const match = sizes.find((button) => !button.disabled && (button.dataset.code || '').toLowerCase() === carriedSize);
      if (match) choose(match);
    }

    /* Size chart -------------------------------------------------------- */

    // The Size charts section, lower on the page, prints this product's chart
    // as a closed dialog marked data-match. Size Chart opens it, and so do
    // See Fit and See Fit Across Sizes until the fit app's class or code is
    // set. A product without a chart shows none of them. The chart is looked
    // up on each click, as the editor redraws that section while it's edited.
    const chartFor = () => document.querySelector('[data-nyl-size-chart][data-match]');
    const chartOpeners = [...section.querySelectorAll('[data-nyl-size-chart-open], [data-nyl-see-fit]')];
    if (chartFor()) {
      chartOpeners.forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => {
          const chart = chartFor();
          if (chart && !chart.open) chart.showModal();
        });
      });
    }

    /* Add to bag --------------------------------------------------------- */

    const form = section.querySelector('[data-nyl-product-form]');
    const addButtons = () => section.querySelectorAll('[data-nyl-add], [data-nyl-sticky-add]');
    const buyError = section.querySelector('[data-nyl-buy-error]');
    let adding = false;

    /* eGift card recipient ----------------------------------------------- */

    // Someone Else sends the card to their email, through Shopify's own gift
    // card delivery: the line carries Shopify's recipient properties and
    // Shopify emails them the card on the day chosen. Each card to someone
    // is its own line, so the button always adds.
    const recipient = section.querySelector('[data-nyl-recipient]');
    const recipientFields = recipient?.querySelector('[data-nyl-recipient-fields]');
    const recipientError = recipient?.querySelector('[data-nyl-recipient-error]');
    const recipientField = (name) => recipient?.querySelector(`[data-nyl-recipient-field="${name}"]`);
    const toSomeone = () => !!recipient?.querySelector('[data-nyl-recipient-mode][value="someone"]')?.checked;

    const localDate = (date) => [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');

    const clearRecipientError = () => {
      if (recipientError) {
        recipientError.hidden = true;
        recipientError.textContent = '';
      }
      recipientFields?.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    };

    const showRecipientError = (text, input) => {
      if (!recipientError) return;
      recipientError.textContent = text;
      recipientError.hidden = !text;
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        recipient.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus({ preventScroll: true });
      }
    };

    // Shopify sends on any day from today to 90 days ahead.
    const datesAllowed = () => {
      const sendOn = recipientField('send_on');
      if (!sendOn) return;
      const today = new Date();
      const last = new Date();
      last.setDate(today.getDate() + 90);
      sendOn.min = localDate(today);
      sendOn.max = localDate(last);
    };

    const syncRecipient = () => {
      if (!recipient) return;
      const on = toSomeone();
      recipientFields.hidden = !on;
      recipientFields.querySelectorAll('input, textarea').forEach((el) => { el.disabled = !on; });
      section.querySelectorAll('[data-nyl-dynamic]').forEach((el) => { el.hidden = on; });
      if (on) datesAllowed();
      else clearRecipientError();
      showInBag();
    };

    recipient?.querySelectorAll('[data-nyl-recipient-mode]').forEach((radio) => {
      radio.addEventListener('change', () => {
        syncRecipient();
        if (toSomeone()) recipientField('email')?.focus();
      });
    });

    const recipientMessage = recipientField('message');
    const recipientCount = recipient?.querySelector('[data-nyl-recipient-count]');
    const countMessage = () => {
      if (recipientCount && recipientMessage) {
        recipientCount.textContent = `${recipientMessage.value.length}/${recipientMessage.maxLength}`;
      }
    };
    recipientMessage?.addEventListener('input', countMessage);
    recipientFields?.addEventListener('input', clearRecipientError);

    // The recipient's details for the bag line: null when the card is for
    // the buyer, false when something needs fixing first.
    const recipientDetails = () => {
      if (!toSomeone()) return null;
      clearRecipientError();
      const email = recipientField('email');
      const name = recipientField('name');
      const sendOn = recipientField('send_on');
      const address = email.value.trim();
      if (!address) {
        showRecipientError(recipientError.dataset.required, email);
        return false;
      }
      if (!email.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(address)) {
        showRecipientError(recipientError.dataset.invalid, email);
        return false;
      }
      datesAllowed();
      if (sendOn.value && (sendOn.value < sendOn.min || sendOn.value > sendOn.max)) {
        showRecipientError(recipientError.dataset.date, sendOn);
        return false;
      }
      const properties = {
        __shopify_send_gift_card_to_recipient: 'on',
        'Recipient email': address,
      };
      if (name.value.trim()) properties['Recipient name'] = name.value.trim();
      if (recipientMessage.value.trim()) properties.Message = recipientMessage.value.trim();
      if (sendOn.value) properties['Send on'] = sendOn.value;
      properties.__shopify_offset = String(new Date().getTimezoneOffset());
      return properties;
    };

    /* Already in the bag -------------------------------------------------- */

    // The size in view already in the bag: both buttons read Already In Cart
    // and open the bag on that line, rather than a second tap quietly making
    // it two. The page arrives knowing this product's sizes in the bag, and
    // reads the bag again whenever anything changes it.
    let inBag = new Set((section.dataset.bagVariants || '').split(',').filter(Boolean));

    showInBag = () => {
      const on = inBag.has(chosenId()) && !toSomeone();
      // The sticky bar has nothing to offer for a size already in the bag.
      const stickyRoot = section.querySelector('[data-nyl-sticky]');
      if (stickyRoot && stickyRoot.hasAttribute('data-in-bag') !== on) stickyRoot.toggleAttribute('data-in-bag', on);
      addButtons().forEach((button) => {
        if (button.hasAttribute('data-in-bag') !== on) button.toggleAttribute('data-in-bag', on);
        const text = on ? button.dataset.labelInBag : button.dataset.labelAdd;
        const label = button.querySelector('[data-nyl-add-label]');
        if (label && text && label.textContent !== text) label.textContent = text;
        if (text && button.matches('[data-nyl-sticky-add]')) button.setAttribute('aria-label', text);
      });
    };

    // A page restored from the back button keeps Someone Else chosen.
    syncRecipient();
    countMessage();

    let reading = null;
    let readAgain = false;
    const readBag = () => {
      if (reading) {
        readAgain = true;
        return;
      }
      reading = fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (!cart?.items) return;
          inBag = new Set(cart.items.map((item) => String(item.variant_id)));
          showInBag();
        })
        .catch(() => {})
        .finally(() => {
          reading = null;
          if (readAgain) {
            readAgain = false;
            readBag();
          }
        });
    };

    ['cart:refresh', 'cart:update', 'nyl:cart:added', 'nyl:cart:removed'].forEach((name) => {
      document.addEventListener(name, readBag);
    });

    // The multi-buy message (the Multi-buy message block: "Add 1 More
    // Activewear Item to Get 20% Off") depends on the bag, so it is drawn
    // again from the server after every change, Discount Ninja's repricing
    // included (sections/nyl-multibuy-message.liquid).
    const multibuy = section.querySelector('[data-nyl-multibuy-pdp]');
    if (multibuy) {
      let drawing = 0;
      const redrawMultibuy = async () => {
        const turn = ++drawing;
        try {
          const response = await fetch(`${multibuy.dataset.url}?section_id=nyl-multibuy-message`, { credentials: 'same-origin' });
          if (!response.ok || turn !== drawing) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = doc.querySelector('.nyl-multibuy-section');
          if (fresh && turn === drawing) multibuy.innerHTML = fresh.innerHTML.trim();
        } catch (error) {
          // The message keeps what it said; the next change or load catches up.
        }
      };
      ['cart:update', 'nyl:cart:added', 'nyl:cart:removed', 'nyl:bag:refresh'].forEach((name) => {
        document.addEventListener(name, redrawMultibuy);
      });
    }
    // Back to this page from another, the bag may have changed in between.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) readBag();
    });

    // The Added to bag panel, on the line this size already has.
    const openBag = async (id) => {
      const detail = { source: 'product', variantId: id, handle, key: '', sections: null, ready: null };
      const opening = new CustomEvent('nyl:cart:open', { cancelable: true, detail });
      if (document.dispatchEvent(opening)) {
        window.location.assign(`${root()}cart`);
        return;
      }
      addButtons().forEach((b) => b.classList.add('is-adding'));
      try {
        await detail.ready;
      } finally {
        addButtons().forEach((b) => b.classList.remove('is-adding'));
      }
    };

    const add = async () => {
      // A £0 product is only ever given as a gift (nyl-product.liquid).
      if (section.querySelector('[data-nyl-not-for-sale]')) return;
      const id = chosenId();
      if (!id) {
        askForSize(true);
        return;
      }
      if (adding) return;
      const gift = recipientDetails();
      if (gift === false) return;
      if (!gift && inBag.has(id)) {
        openBag(id);
        return;
      }
      adding = true;
      addButtons().forEach((b) => b.classList.add('is-adding'));
      if (buyError) buyError.hidden = true;

      // The panel opens now on the piece being added, busy, rather than
      // after the bag answers, which takes a second or more.
      const early = window.nylPanelAdding?.(handle, id);

      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          // The Added to bag panel's fields, when it is on the page, have
          // Shopify render it in this same request (nyl-quick-add.js).
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: 1, ...(gift ? { properties: gift } : {}) }],
            ...(window.nylPanelSections?.(handle, id) || {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          // A recipient Shopify turns down comes back as field errors.
          const fields = data.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat().join(' ') : '';
          const text = (typeof data.description === 'string' && data.description) || fields || data.message || '';
          const failure = new Error(text);
          if (gift && fields) {
            // Said once, beside the field Shopify named.
            showRecipientError(text, recipientField(Object.keys(data.errors)[0]));
            failure.shown = true;
          }
          throw failure;
        }

        inBag.add(String(id));
        showInBag();

        document.dispatchEvent(new CustomEvent('nyl:cart:added', { bubbles: true, detail: { handles: [handle] } }));

        // The Added to bag panel answers nyl:cart:open and keeps the visitor
        // on the page, with the bag's subtotal and the Wear it with row. The
        // button stays busy until the panel is up. When nothing answers it,
        // the cart page opens.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        const detail = {
          source: 'product',
          variantId: id,
          handle,
          key: data.items?.[0]?.key,
          sections: data.sections,
          ready: null,
        };
        const opening = new CustomEvent('nyl:cart:open', { cancelable: true, detail });
        if (document.dispatchEvent(opening)) window.location.assign(`${root()}cart`);
        else await detail.ready;
      } catch (error) {
        early?.failed();
        if (buyError && !error.shown) {
          buyError.textContent = error.message || buyError.dataset.fallback || '';
          buyError.hidden = !buyError.textContent;
        }
      } finally {
        adding = false;
        addButtons().forEach((b) => b.classList.remove('is-adding'));
      }
    };

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      add();
    });

    // A pointer on the button is as good a sign as a size pick, and covers
    // products with only one size, where nothing is picked.
    addButtons().forEach((button) => {
      ['pointerenter', 'focus'].forEach((type) => button.addEventListener(type, () => {
        const id = chosenId();
        if (id && !inBag.has(id)) window.nylPanelPrefetch?.(handle, id);
      }));
    });

    // Shop Pay reads the size from its form. Until one is chosen the press is
    // stopped before it reaches the button, and the size row asks instead.
    const dynamic = section.querySelector('[data-nyl-dynamic]');
    dynamic?.addEventListener('click', (event) => {
      if (chosenId()) return;
      event.preventDefault();
      event.stopPropagation();
      askForSize(true);
    }, true);

    /* Apps that may render nothing ---------------------------------------- */

    // Shop Pay, Klarna and the like fill in after load, and for some visitors
    // fill in nothing at all. Their spacing follows what actually shows.
    if ('ResizeObserver' in window) {
      section.querySelectorAll('[data-nyl-dynamic], .nyl-product__block--app').forEach((el) => {
        const sync = () => el.toggleAttribute('data-empty', el.getBoundingClientRect().height < 1);
        new ResizeObserver(sync).observe(el);
        sync();
      });
    }

    /* Tabs --------------------------------------------------------------- */

    section.querySelectorAll('[data-nyl-tabs]').forEach((tabs) => {
      const list = [...tabs.querySelectorAll('[data-nyl-tab]')];
      const select = (tab, focus = false) => {
        list.forEach((other) => {
          const on = other === tab;
          other.setAttribute('aria-selected', String(on));
          other.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(other.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
      };
      list.forEach((tab, n) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
          const keys = { ArrowRight: n + 1, ArrowLeft: n - 1, Home: 0, End: list.length - 1 };
          if (!(event.key in keys)) return;
          event.preventDefault();
          select(list[(keys[event.key] + list.length) % list.length], true);
        });
      });
    });

    /* Wear It With ------------------------------------------------------- */

    section.querySelectorAll('[data-nyl-wear]').forEach((wear) => {
      const list = wear.querySelector('[data-nyl-wear-list]');
      const back = wear.querySelector('[data-nyl-wear-prev]');
      const forward = wear.querySelector('[data-nyl-wear-next]');
      if (!list) return;
      const step = () => (list.firstElementChild?.getBoundingClientRect().width || 153) + 20;
      const update = () => {
        const max = list.scrollWidth - list.clientWidth - 1;
        if (back) back.hidden = list.scrollLeft <= 1;
        if (forward) forward.hidden = list.scrollLeft >= max;
      };
      back?.addEventListener('click', () => list.scrollBy({ left: -step(), behavior: 'smooth' }));
      forward?.addEventListener('click', () => list.scrollBy({ left: step(), behavior: 'smooth' }));
      list.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      wear.addEventListener('nyl:wear:changed', update);
      update();
    });

    // A cross sell already in the bag has done its job, so it leaves the
    // feed: straight after it is added from here or anywhere else on the
    // page, and on arrival for whatever the bag already holds.
    // The next piece in the pool takes the place of each one that leaves,
    // so the feed stays full for as long as the pool lasts. A piece You
    // might also like is already showing stays back, so the two rows never
    // repeat each other.
    const inYouMightAlsoLike = (item) => {
      const handle = item.dataset.handle;
      return !!handle && !!document.querySelector(`[data-nyl-ymal] [data-product-handle="${CSS.escape(handle)}"]`);
    };
    const refillWear = (wear) => {
      const limit = Number(wear.dataset.limit) || 6;
      const shown = () => wear.querySelectorAll('[data-nyl-wear-item]:not([data-nyl-wear-reserve])').length;
      for (const next of wear.querySelectorAll('[data-nyl-wear-reserve]')) {
        if (shown() >= limit) break;
        if (inYouMightAlsoLike(next)) continue;
        next.removeAttribute('data-nyl-wear-reserve');
        next.hidden = false;
      }
    };

    const dropFromWear = (handles) => {
      if (!handles || !handles.length) return;
      section.querySelectorAll('[data-nyl-wear]').forEach((wear) => {
        let removed = false;
        wear.querySelectorAll('[data-nyl-wear-item]').forEach((item) => {
          if (handles.includes(item.dataset.handle)) {
            item.remove();
            removed = true;
          }
        });
        if (!removed) return;
        refillWear(wear);
        if (!wear.querySelector('[data-nyl-wear-item]:not([data-nyl-wear-reserve])')) wear.hidden = true;
        wear.dispatchEvent(new CustomEvent('nyl:wear:changed'));
      });
    };

    if (section.querySelector('[data-nyl-wear-item]')) {
      document.addEventListener('nyl:cart:added', (event) => dropFromWear(event.detail?.handles));
      fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => dropFromWear((cart?.items || []).map((line) => line.handle)))
        .catch(() => {
          // The feed simply stays whole.
        });
    }

    /* Sticky bar ---------------------------------------------------------- */

    const sticky = section.querySelector('[data-nyl-sticky-bar]');
    const buy = section.querySelector('[data-nyl-buy]');

    // The size in the bar opens the sizes above it. A size picked there is
    // chosen exactly as in the size row, so the page, the address and the
    // bar all agree, and the bag then adds it.
    const stickySizeToggle = section.querySelector('[data-nyl-sticky-size]');
    const stickySizes = section.querySelector('[data-nyl-sticky-sizes]');
    const setStickySizes = (open) => {
      if (!stickySizeToggle || !stickySizes) return;
      stickySizes.hidden = !open;
      stickySizeToggle.setAttribute('aria-expanded', String(open));
      if (open) {
        const target = stickySizes.querySelector('[data-nyl-sticky-size-option].is-chosen')
          || stickySizes.querySelector('[data-nyl-sticky-size-option]:not(:disabled)');
        target?.focus({ preventScroll: true });
      }
    };
    stickySizeToggle?.addEventListener('click', () => setStickySizes(stickySizes.hidden));
    stickySizes?.addEventListener('click', (event) => {
      const option = event.target.closest('[data-nyl-sticky-size-option]');
      if (!option || option.disabled) return;
      const match = sizes.find((button) => button.dataset.variantId === option.dataset.variantId);
      if (match) choose(match);
      setStickySizes(false);
      stickySizeToggle?.focus({ preventScroll: true });
    });
    document.addEventListener('click', (event) => {
      if (stickySizes && !stickySizes.hidden && !event.target.closest('[data-nyl-sticky]')) setStickySizes(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !stickySizes || stickySizes.hidden) return;
      setStickySizes(false);
      stickySizeToggle?.focus({ preventScroll: true });
    });

    if (sticky && buy && 'IntersectionObserver' in window) {
      let pastButtons = false;
      let atFooter = false;
      const render = () => {
        const stuck = pastButtons && !atFooter;
        sticky.dataset.stuck = String(stuck);
        if (!stuck) setStickySizes(false);
      };

      new IntersectionObserver(([entry]) => {
        // Only once the buttons have gone up and out of view, not before
        // the visitor has reached them.
        pastButtons = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        render();
      }).observe(buy);

      const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
      if (footer) {
        new IntersectionObserver(([entry]) => {
          atFooter = entry.isIntersecting;
          render();
        }, { rootMargin: '200px 0px 0px 0px' }).observe(footer);
      }

      // Without a size, the bag opens the sizes here rather than sending the
      // visitor back up the page.
      section.querySelector('[data-nyl-sticky-add]')?.addEventListener('click', () => {
        if (!chosenId() && stickySizes) {
          setStickySizes(true);
          return;
        }
        add();
      });
    }
  };

  const start = () => document.querySelectorAll('[data-nyl-product]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', start);
})();
